import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from 'app/cluster-styles/useStyles';
import { CLUSTER_STATES } from 'app/main/util/clusterUtils';
import { IMAGE_TYPE } from '../../util/enums';
import * as StackActions from 'app/main/stacks/store/actions/stacks.actions.js';
import * as ActionsDialog from 'app/store/actions';
import * as Actions from '../store/actions';
import { getClusterSettings, getOrgClusterSettings } from 'app/main/settings/store/actions';

import useMarketplace from './useMarketplace';
import useClusterOwner from './useClusterOwner';
import useProviderData from './useProviderData';
import { useInstanceAvailability } from './useInstanceAvailability';
import useQuotas from './useQuotas';

/**
 * Shared form lifecycle hook for all cluster provider forms.
 * Encapsulates: state, Redux selectors, hooks composition, init effect, handlers.
 *
 * @param {string} provider - 'ibm'|'azure'|'gcp'|'oracle'|'aws'
 * @param {object} props - component props (type, oldForm, marketId)
 * @param {object} formHelpers - { toApiShape, initNewForm, initEditForm }
 * @param {function} [extraValidation] - (form) => boolean for provider-specific checks (runs AFTER base checks)
 * @param {function} [customValidation] - (form) => boolean to REPLACE base validation entirely (for providers like MaaS)
 * @param {object} [availabilityOptions] - extra options for useInstanceAvailability (e.g. zoneId override)
 * @param {string} [zoneField] - form field name to use as zoneId (defaults to 'zoneId')
 * @param {function} [onInit] - extra dispatches to run on mount (e.g. getAcmImages for MaaS)
 */
export default function useClusterForm({ provider, props, formHelpers, extraValidation, customValidation, availabilityOptions = {}, zoneField, onInit }) {
    const dispatch = useDispatch();
    const classes = useStyles(props);

    // Redux state
    const clusterSettings = useSelector(({ settings }) => settings.settings.clusterSettings);
    const orgClusterSettings = useSelector(({ settings }) => settings.settings.orgClusterSettings);
    const cluster = useSelector(({ clusters }) => clusters.cluster.cluster);
    const loading = useSelector(({ fuse }) => fuse.settings.loading);
    const { volSizeLimits, instanceSizes } = useProviderData(provider);

    // Form state
    const [form, setForm] = useState(props.oldForm || null);
    const setField = useCallback((keyOrChanges, value) => {
        setForm(prev => ({
            ...prev,
            ...(typeof keyOrChanges === 'string' ? { [keyOrChanges]: value } : keyOrChanges),
        }));
    }, []);

    // Marketplace
    const marketplace = useMarketplace({ provider, form, setField, propsMarketId: props.marketId });

    // UI state
    const [advanced, setAdvanced] = useState(false);
    const { selectedOwner, setSelectedOwner } = useClusterOwner(form, props.type);
    const disableInputs = props.type === 'edit' && Boolean(form?.state) && form.state !== CLUSTER_STATES.DESTROYED;

    // Validation — customValidation fully replaces base checks, extraValidation extends them
    const isFormValid = useMemo(() => {
        if (!form) return false;
        if (customValidation) return customValidation(form);
        if (!form.displayName) return false;
        if (!form.credentialId) return false;
        if (!form.regionId) return false;
        if (!form.serverInstanceId) return false;
        const imageType = form.serverImageType || IMAGE_TYPE.HEIDI_STACK;
        if (imageType === IMAGE_TYPE.HEIDI_STACK && !form.serverImageName) return false;
        if (extraValidation && !extraValidation(form)) return false;
        return true;
    }, [form, extraValidation, customValidation]);

    // Instance availability
    const computeNodeInstanceTypes = useMemo(
        () => (form?.nodeGroups || []).map(g => g.instance_id).filter(Boolean),
        [form?.nodeGroups]
    );
    const availability = useInstanceAvailability({
        provider,
        credentialId: form?.credentialId,
        region: form?.regionId,
        zoneId: availabilityOptions.zoneId !== undefined ? availabilityOptions.zoneId : form?.[zoneField || 'zoneId'],
        headNodeInstance: form?.serverInstanceId,
        computeNodeInstances: computeNodeInstanceTypes,
        enabled: Boolean(form?.credentialId && form?.regionId),
    });

    // Quotas
    const quotas = useQuotas(provider, form);

    // === EFFECTS ===

    useEffect(() => {
        dispatch(StackActions.getStacks());
        dispatch(getClusterSettings());
        dispatch(getOrgClusterSettings());
        if (onInit) onInit(dispatch);
    }, [dispatch]);

    // Initialize form once
    const formInitialized = useRef(false);
    useEffect(() => {
        if (formInitialized.current) return;
        if (!clusterSettings) return;

        if (props.type === 'new') {
            formInitialized.current = true;
            setForm(formHelpers.initNewForm(provider, {
                marketplaceConfig: marketplace.config,
                clusterSettings,
                orgClusterSettings,
                marketId: marketplace.marketId,
            }));
        } else if (props.type === 'edit' && cluster) {
            formInitialized.current = true;
            setForm(formHelpers.initEditForm(cluster, { clusterSettings, orgClusterSettings }));
        }
    }, [props.type, cluster, clusterSettings, orgClusterSettings, marketplace.config, marketplace.marketId]);

    // === HANDLERS ===

    const handleClose = useCallback(() => {
        dispatch(ActionsDialog.closeDialog());
    }, [dispatch]);

    const handleSubmit = useCallback(() => {
        dispatch(Actions.saveCluster(formHelpers.toApiShape(form)));
        handleClose();
    }, [dispatch, form, handleClose]);

    const handleEdit = useCallback(() => {
        dispatch(Actions.editCluster(formHelpers.toApiShape(form)));
    }, [dispatch, form]);

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleClose]);

    return {
        form, setForm, setField,
        advanced, setAdvanced,
        classes, loading,
        marketplace,
        selectedOwner, setSelectedOwner,
        disableInputs,
        isFormValid,
        volSizeLimits, instanceSizes,
        computeNodeInstanceTypes,
        availability, quotas,
        handleClose, handleSubmit, handleEdit,
        dispatch,
    };
}
